import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, MessageCircle, UserPlus, UserCheck, Users, Megaphone } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useSupabaseSession } from "@/lib/SupabaseSessionContext";
import { useT } from "@/lib/i18n";

const ICON = {
  message: MessageCircle,
  friend_request: UserPlus,
  squad_invite: Users,
  request_accepted: UserCheck,
  system: Megaphone,
};

const TINT = {
  message: "text-cyan-300",
  friend_request: "text-emerald-300",
  squad_invite: "text-fuchsia-300",
  request_accepted: "text-amber-300",
  system: "text-zinc-300",
};

export default function NotificationBell() {
  const { ready, supabaseUserId } = useSupabaseSession();
  const { lang } = useT();
  const isDE = (lang || "de").startsWith("de");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    if (!ready || !supabase || !supabaseUserId) return;
    try {
      const { data } = await supabase
        .from("notifications")
        .select("id, kind, title, body, link, is_read, created_at")
        .order("created_at", { ascending: false })
        .limit(20);
      const list = data || [];
      setItems(list);
      setUnread(list.filter((i) => !i.is_read).length);
    } catch (_e) { /* noop */ }
  }, [ready, supabaseUserId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Realtime subscription
  useEffect(() => {
    if (!ready || !supabase || !supabaseUserId) return;
    const ch = supabase
      .channel(`notif:${supabaseUserId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${supabaseUserId}` },
        () => refresh(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [ready, supabaseUserId, refresh]);

  const markAllRead = async () => {
    if (!supabase) return;
    try {
      await supabase.from("notifications").update({ is_read: true }).eq("is_read", false);
      refresh();
    } catch (_e) { /* noop */ }
  };

  const markRead = async (id) => {
    if (!supabase) return;
    try {
      await supabase.from("notifications").update({ is_read: true }).eq("id", id);
      refresh();
    } catch (_e) { /* noop */ }
  };

  if (!ready) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell"
        className="relative w-10 h-10 rounded-full grid place-items-center text-zinc-300 hover:text-neon hover:bg-white/5 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <>
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-rose-500/40 animate-ping" />
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center"
              data-testid="notif-bell-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          </>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-950/95 shadow-2xl z-40 backdrop-blur"
            data-testid="notif-dropdown"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 sticky top-0 bg-zinc-950/95 backdrop-blur">
              <h3 className="font-display font-bold text-sm">
                {isDE ? "Benachrichtigungen" : "Notifications"}
              </h3>
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-[11px] text-neon hover:underline inline-flex items-center gap-1"
                  data-testid="notif-mark-all-read"
                >
                  <Check size={11} /> {isDE ? "Alle als gelesen" : "Mark all read"}
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-zinc-500">
                {isDE ? "Keine neuen Benachrichtigungen" : "No new notifications"}
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {items.map((n) => {
                  const Icon = ICON[n.kind] || Bell;
                  const tint = TINT[n.kind] || "text-zinc-300";
                  const body = (
                    <div
                      className={`flex gap-3 px-4 py-3 hover:bg-white/5 transition-colors ${n.is_read ? "opacity-60" : ""}`}
                    >
                      <span className={`w-8 h-8 rounded-lg bg-white/5 grid place-items-center shrink-0 ${tint}`}>
                        <Icon size={14} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-zinc-100 truncate">{n.title}</p>
                          {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-neon shrink-0" />}
                        </div>
                        {n.body && <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.body}</p>}
                        <p className="text-[10px] text-zinc-500 mt-1">{timeAgo(n.created_at, isDE)}</p>
                      </div>
                    </div>
                  );
                  return (
                    <li key={n.id} data-testid={`notif-item-${n.id}`}>
                      {n.link ? (
                        <Link to={n.link} onClick={() => { markRead(n.id); setOpen(false); }}>
                          {body}
                        </Link>
                      ) : (
                        <button type="button" onClick={() => markRead(n.id)} className="w-full text-left">
                          {body}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="px-4 py-2 border-t border-white/5 sticky bottom-0 bg-zinc-950/95 backdrop-blur">
              <Link
                to="/app/notifications"
                onClick={() => setOpen(false)}
                className="block text-center text-xs text-neon hover:underline py-1"
              >
                {isDE ? "Alle ansehen" : "View all"}
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function timeAgo(iso, isDE) {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return isDE ? "gerade eben" : "just now";
    if (diff < 3600) return isDE ? `vor ${Math.floor(diff / 60)} Min` : `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return isDE ? `vor ${Math.floor(diff / 3600)} Std` : `${Math.floor(diff / 3600)} h ago`;
    return isDE ? `vor ${Math.floor(diff / 86400)} Tg` : `${Math.floor(diff / 86400)} d ago`;
  } catch (_e) { return ""; }
}
